import {inject, Injectable, signal, computed} from '@angular/core';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';

import {Observable, of, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {FirestoreService} from './firestore.service';
import {
  Auth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  signInAnonymously,
  signInWithCustomToken,
  signOut, sendPasswordResetEmail, authState
} from '@angular/fire/auth';

/**
 * Service responsible for managing user authentication state, token persistence,
 * anonymous sessions, custom token sign-ins, password resets, and user roles.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /** Observable stream of the authenticated user and custom token claims. */
  loggedInUserFromAuthService$: Observable<any>;

  /** Signal holding the current authenticated user's profile and client ID. */
  loggedInUserSignal = signal<any>(null);

  /** Signal holding the custom UID of non-anonymous authenticated users. */
  loggedInCustomUIDSignal = signal<string | null>(null);

  /** Signal indicating whether the current session is anonymous. */
  isAnonymousSignal = signal<boolean>(false);

  /**
   * Getter for the anonymous authentication flag.
   * @returns `true` if current user is anonymous, otherwise `false`.
   */
  get isAnonymous(): boolean {
    return this.isAnonymousSignal();
  }

  /**
   * Setter for the anonymous authentication flag.
   * @param val - Boolean value indicating if session is anonymous.
   */
  set isAnonymous(val: boolean) {
    this.isAnonymousSignal.set(val);
  }

  /**
   * Getter for the logged-in user's custom UID.
   * @returns User UID string or null if not authenticated / anonymous.
   */
  get loggedInCustomUID(): string | null {
    return this.loggedInCustomUIDSignal();
  }

  /**
   * Setter for the logged-in user's custom UID.
   * @param val - UID string or null.
   */
  set loggedInCustomUID(val: string | null) {
    this.loggedInCustomUIDSignal.set(val);
  }

  private router = inject(Router);
  private firestoreService = inject(FirestoreService);
  private route = inject(ActivatedRoute);
  auth: Auth = inject(Auth);

  private loggedInUserFromAuthServiceSubscription?: Subscription;

  constructor() {
    this.loggedInUserFromAuthService$ = authState(this.auth).pipe(
        switchMap(async (user: any) => {
          if (user) {
            if (!user.isAnonymous) {
              this.loggedInCustomUID = user.uid;
            }
            this.firestoreService.setLoggedInUserUid(user.uid);
            this.isAnonymous = user.isAnonymous;
            const idTokenRes = await user.getIdTokenResult();
            const claims = idTokenRes.claims;
              return {
                uid: user.uid,
                clientId: claims.clientId
              };

          } else {
            return of(null);
          }
        })
    );
    this.loggedInUserFromAuthServiceSubscription = this.loggedInUserFromAuthService$.subscribe(userDocData => {
      this.loggedInUserSignal.set(userDocData);
    });
  }

  // Users can have 3 roles - admin(type=client), manager(type=client), developer(type=developer)

  /**
   * Checks whether there is an active authenticated user.
   * @returns `true` if user is signed in, `false` otherwise.
   */
  get isAuthenticated(): boolean {
    return this.loggedInUserSignal() !== null;
  }

  /**
   * Signs in the user anonymously with browser local persistence.
   * @returns Promise resolving to the anonymous user credential.
   */
  async signInAnonymously() {
    return setPersistence(this.auth, browserLocalPersistence).then(() => {
      return signInAnonymously(this.auth);
    });
  }

  /**
   * Signs in the user with a custom authentication token.
   * @param token - Custom JWT authentication token.
   * @returns Promise resolving to the user credential.
   */
  async signIn(token: string) {
    return setPersistence(this.auth, browserLocalPersistence).then(() => {
      console.log(`token: ${token}`)
      return signInWithCustomToken(this.auth, token);
    });
  }

  /**
   * Sends a password reset email to the specified email address.
   * @param email - Target user email address.
   * @returns Promise resolving when reset email is sent.
   */
  async resetPassword(email: string) {
    return sendPasswordResetEmail(this.auth, email)
  }

  /**
   * Signs out the current user, clears auth session, and navigates to the root route.
   * @returns Promise resolving when sign-out is complete.
   */
  async signOut() {

    signOut(this.auth).then(() => {
      console.log('signed out');
      this.router.navigate(['']);
    });
  }

}

